import type { VectorKbConfig, VectorKbKnowledgeSource } from "../types";
import type { VectorKbToolParameters } from "./vectorKbInputSchema";

/** Matches Pacific.Agentic.Api ``_EXPANSION_VARIANT_COUNT``. */
export const FUSION_EXPANSION_VARIANT_COUNT = 3;

/**
 * When ``rerank_pool`` is omitted, warn if fan-out could send more than this many
 * chunks to Cohere. Matches the API's former hardcoded shortlist (50) and the
 * documented example for cost control — rerank bills per document sent.
 */
export const FUSION_RERANK_POOL_COST_WARNING_THRESHOLD = 50;

export type FusionPipelineInput = {
  knowledgeSourceCount: number;
  candidateK: number;
  defaultTopK: number;
  maxTopK: number;
  maxAlternateQueries: number;
  expandWhenEmpty: boolean;
  includeAlternateQueries: boolean;
  includeTopK: boolean;
  minScore: number | null;
  rerankPool: number | null;
};

export type FusionPipelineStep = {
  id: string;
  title: string;
  detail: string;
  countLabel: string;
};

export type FusionPipelineModel = {
  ready: boolean;
  formula: string;
  searchesNote: string;
  searchCount: number;
  knowledgeSourceCount: number;
  candidateK: number;
  listCount: number;
  candidateCeiling: number;
  rerankPoolCeiling: number;
  defaultTopK: number;
  minScore: number | null;
  rerankPoolCostWarning: string | null;
  steps: FusionPipelineStep[];
};

export function buildRerankPoolCostWarning(
  candidateCeiling: number,
  rerankPool: number | null,
): string | null {
  if (
    rerankPool != null ||
    candidateCeiling <= FUSION_RERANK_POOL_COST_WARNING_THRESHOLD
  ) {
    return null;
  }
  return (
    `Without a rerank pool cap, up to ${candidateCeiling} fused chunks could be sent to Cohere rerank ` +
    "(often fewer after merge and dedupe). Rerank cost and latency grow with the number of documents " +
    `sent; the API previously shortlisted at ${FUSION_RERANK_POOL_COST_WARNING_THRESHOLD}. ` +
    "Consider setting rerank_pool when fan-out is large."
  );
}

export function countConfiguredKnowledgeSources(
  sources: VectorKbKnowledgeSource[],
): number {
  return sources.filter((source) => source.id.trim() !== "").length;
}

function expansionAlternateCount(maxAlternateQueries: number): number {
  return Math.min(
    FUSION_EXPANSION_VARIANT_COUNT,
    Math.max(0, maxAlternateQueries),
  );
}

function maxSearchCount(input: FusionPipelineInput): number {
  const maxAlts = Math.max(0, input.maxAlternateQueries);
  if (input.includeAlternateQueries) {
    return 1 + maxAlts;
  }
  if (input.expandWhenEmpty) {
    return 1 + expansionAlternateCount(maxAlts);
  }
  return 1;
}

function searchesNote(input: FusionPipelineInput): string {
  const expansionCount = expansionAlternateCount(input.maxAlternateQueries);
  if (input.includeAlternateQueries && input.expandWhenEmpty) {
    return `1 question plus up to ${input.maxAlternateQueries} agent-supplied alternate searches. If the agent omits them, expansion adds up to ${expansionCount} instead. Identifier-style questions skip expansion.`;
  }
  if (input.includeAlternateQueries) {
    return `1 question plus up to ${input.maxAlternateQueries} agent-supplied alternate searches. Expansion is off, so omitting them means question-only.`;
  }
  if (input.expandWhenEmpty && expansionCount > 0) {
    if (input.maxAlternateQueries < FUSION_EXPANSION_VARIANT_COUNT) {
      return `1 question plus up to ${expansionCount} expanded searches (capped by max alternate queries). Identifier-style questions skip expansion.`;
    }
    return `1 question plus up to ${expansionCount} expanded searches (expansion generates at most ${FUSION_EXPANSION_VARIANT_COUNT}). Identifier-style questions skip expansion.`;
  }
  return "Question only — no alternate searches.";
}

function pluralize(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

function topKCountLabel(input: FusionPipelineInput): string {
  if (input.includeTopK) {
    return `${input.defaultTopK} (up to ${input.maxTopK})`;
  }
  return String(input.defaultTopK);
}

function agentCountLabel(input: FusionPipelineInput): string {
  if (input.minScore == null) {
    return input.includeTopK
      ? `${input.defaultTopK} to the agent (up to ${input.maxTopK})`
      : `${input.defaultTopK} to the agent`;
  }
  return input.includeTopK
    ? `0–${input.defaultTopK} to the agent (up to ${input.maxTopK})`
    : `0–${input.defaultTopK} to the agent`;
}

export function describeFusionPipelineFromForm(
  config: VectorKbConfig,
  toolParameters: Pick<
    VectorKbToolParameters,
    "includeAlternateQueries" | "includeTopK"
  >,
): FusionPipelineModel {
  return describeFusionPipeline({
    knowledgeSourceCount: countConfiguredKnowledgeSources(
      config.knowledge_sources,
    ),
    candidateK: config.candidate_k,
    defaultTopK: config.default_top_k,
    maxTopK: config.max_top_k,
    maxAlternateQueries: config.max_alternate_queries,
    expandWhenEmpty: config.expand_when_empty,
    includeAlternateQueries: toolParameters.includeAlternateQueries,
    includeTopK: toolParameters.includeTopK,
    minScore: config.min_score ?? null,
    rerankPool: config.rerank_pool ?? null,
  });
}

export function describeFusionPipeline(
  input: FusionPipelineInput,
): FusionPipelineModel {
  const knowledgeSourceCount = Math.max(0, input.knowledgeSourceCount);
  const candidateK = Math.max(0, input.candidateK);
  const searchCount = maxSearchCount(input);
  const listCount = searchCount * knowledgeSourceCount;
  const candidateCeiling = listCount * candidateK;
  const ready = knowledgeSourceCount > 0 && candidateCeiling > 0;

  if (!ready) {
    return {
      ready: false,
      formula: "",
      searchesNote: searchesNote(input),
      searchCount,
      knowledgeSourceCount,
      candidateK,
      listCount: 0,
      candidateCeiling: 0,
      rerankPoolCeiling: 0,
      defaultTopK: input.defaultTopK,
      minScore: input.minScore,
      rerankPoolCostWarning: null,
      steps: [],
    };
  }

  const rerankPoolCeiling =
    input.rerankPool == null
      ? candidateCeiling
      : Math.min(input.rerankPool, candidateCeiling);
  const rerankPoolStep =
    input.rerankPool == null
      ? {
          detail:
            "Every fused chunk is sent to Cohere rerank (no shortlist cap).",
          countLabel: "all fused",
        }
      : {
          detail: `Only the top ${input.rerankPool} fused chunks (by RRF rank) are sent to Cohere.`,
          countLabel: `≤ ${rerankPoolCeiling}`,
        };
  const kbLabel = pluralize(
    knowledgeSourceCount,
    "knowledge base",
    "knowledge bases",
  );

  const steps: FusionPipelineStep[] = [
    {
      id: "searches",
      title: "Search strings",
      detail: "The question plus any alternate or expanded search strings.",
      countLabel: String(searchCount),
    },
    {
      id: "knowledge-bases",
      title: "Each knowledge base",
      detail:
        "Every search string runs against every configured knowledge base.",
      countLabel: String(knowledgeSourceCount),
    },
    {
      id: "candidates",
      title: "First-pass candidates",
      detail: `Each search × knowledge base pulls up to ${candidateK} chunks.`,
      countLabel: `up to ${candidateCeiling}`,
    },
    {
      id: "fuse",
      title: "Merge ranked lists",
      detail:
        "Reciprocal rank fusion combines those lists by chunk id. Overlap across searches and knowledge bases collapses this count.",
      countLabel: `≤ ${candidateCeiling}`,
    },
    {
      id: "dedupe",
      title: "Drop identical text",
      detail: "Chunks with the same text keep one copy.",
      countLabel: "fewer still",
    },
    {
      id: "rerank-pool",
      title: "Rerank pool",
      detail: rerankPoolStep.detail,
      countLabel: rerankPoolStep.countLabel,
    },
    {
      id: "rerank",
      title: "Rerank against the question",
      detail: input.includeTopK
        ? `One Cohere pass against the original question, then keep default top_k (${input.defaultTopK}) or the agent's top_k up to ${input.maxTopK}.`
        : `One Cohere pass against the original question, then keep top_k (${input.defaultTopK}).`,
      countLabel: topKCountLabel(input),
    },
    {
      id: "min-score",
      title:
        input.minScore == null
          ? "Send to the agent"
          : `Min score ${input.minScore}`,
      detail:
        input.minScore == null
          ? "No score floor — the agent receives the ranked top_k window."
          : `After ranking, drop chunks scored below ${input.minScore}. This can shrink the result below top_k, including to zero.`,
      countLabel: agentCountLabel(input),
    },
  ];

  return {
    ready: true,
    formula: `Up to ${searchCount} ${searchCount === 1 ? "search" : "searches"} × ${kbLabel} × ${candidateK} candidates = ${candidateCeiling} chunks into the merge`,
    searchesNote: searchesNote(input),
    searchCount,
    knowledgeSourceCount,
    candidateK,
    listCount,
    candidateCeiling,
    rerankPoolCeiling,
    defaultTopK: input.defaultTopK,
    minScore: input.minScore,
    rerankPoolCostWarning: buildRerankPoolCostWarning(
      candidateCeiling,
      input.rerankPool,
    ),
    steps,
  };
}
